import PropTypes from 'prop-types';

import { ReactComponent as ChevronLeftIcon } from 'assets/icons/chevron-left.svg';
import { ReactComponent as ChevronRightIcon } from 'assets/icons/chevron-right.svg';

function getVisiblePages(currentPage, totalPages, maxVisiblePages) {
	const half = Math.floor(maxVisiblePages / 2);
	let start = Math.max(currentPage - half, 1);
	let end = Math.min(start + maxVisiblePages - 1, totalPages);

	if (end - start + 1 < maxVisiblePages) {
		start = Math.max(end - maxVisiblePages + 1, 1);
	}

	const pages = [];
	for (let i = start; i <= end; i++) {
		pages.push(i);
	}
	return pages;
}

const ICON_BUTTON_CLASSES =
	'tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-[2px] tw-text-text-primary hover:enabled:tw-bg-surface-layer-02 disabled:tw-cursor-not-allowed disabled:tw-text-text-disabled';

/**
 * Redesigned (VWA-2818) pagination control, Tailwind-styled per the Figma
 * "Cake Pop" pagination pattern (frame 34:72, nodes #34:333-344): chevrons
 * + numbered circles, active page filled dark.
 *
 * Deliberately a new file rather than a restyle of the legacy `Paginator`
 * component (`components/Paginator`) — that one is reused across ~35 legacy
 * Bootstrap pages, so restyling it in place would apply the redesign to
 * screens this migration hasn't reached yet. Opt in to this component from
 * redesigned pages instead.
 */
const PaginatorV3 = ({ currentPage, totalPages, onPageChange, maxVisiblePages = 5 }) => {
	if (!totalPages || totalPages <= 1) return null;

	const goTo = (page) => {
		if (page >= 1 && page <= totalPages && page !== currentPage) {
			onPageChange(page);
		}
	};

	const pages = getVisiblePages(currentPage, totalPages, maxVisiblePages);

	return (
		<nav aria-label="Pagination" className="tw-flex tw-items-center tw-justify-center tw-gap-1">
			<button
				type="button"
				className={ICON_BUTTON_CLASSES}
				onClick={() => goTo(currentPage - 1)}
				disabled={currentPage === 1}
				aria-label="Previous page"
			>
				<ChevronLeftIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
			</button>
			{pages.map((page) => {
				const isActive = page === currentPage;
				return (
					<button
						key={page}
						type="button"
						onClick={() => goTo(page)}
						aria-current={isActive ? 'page' : undefined}
						aria-label={`Page ${page}`}
						className={[
							'tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-[2px] tw-text-xs tw-font-medium',
							isActive
								? 'tw-bg-[#161616] tw-text-white tw-font-bold'
								: 'tw-text-text-primary hover:tw-bg-surface-layer-02',
						].join(' ')}
					>
						{page}
					</button>
				);
			})}
			<button
				type="button"
				className={ICON_BUTTON_CLASSES}
				onClick={() => goTo(currentPage + 1)}
				disabled={currentPage === totalPages}
				aria-label="Next page"
			>
				<ChevronRightIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
			</button>
		</nav>
	);
};

PaginatorV3.propTypes = {
	currentPage: PropTypes.number.isRequired,
	totalPages: PropTypes.number.isRequired,
	onPageChange: PropTypes.func.isRequired,
	maxVisiblePages: PropTypes.number,
};

export default PaginatorV3;
